// components/AccommodationCard.tsx
import React from 'react';
import { FaUsers, FaBed, FaStar, FaChild, FaDollarSign } from 'react-icons/fa';

interface Bed {
  type: string;
  quantity: number;
}

interface Item {
  uid?: string;
  label: string;
  name: string;
  type?: string;
  capacity?: number;
  childCapacity?: number;
  beds?: Bed[];
  provider?: string;
  reservedDates?: string[];
  description?: string;
  image?: string;
  bgImage?: string;
  onImageButtons?: { label: string; value: string; url: string; style: string }[];
  details: { label: string; value: string; icon: string }[];
  rating?: number;
  pricing?: {
    tiers?: { [key: string]: number };
    seasonal?: {
      [key: string]: {
        dates: [string, string];
        multiplier: number;
        label?: string;
      };
    };
    date_specific?: Array<{
      date: string;
      price: number;
      label?: string;
    }>;
  };
}

interface AccommodationCardProps {
  item: Item;
  onShowItem: (item: Item) => void;
}

const AccommodationCard = ({ item, onShowItem }: AccommodationCardProps) => {
  // Get best price from pricing tiers
  const getBestPrice = () => {
    if (item.pricing?.tiers) {
      const tiers = item.pricing.tiers;
      const prices = Object.values(tiers);
      const minPrice = Math.min(...prices);
      return `$${minPrice}`;
    }
    if (item.onImageButtons && item.onImageButtons.length > 0) {
      const prices = item.onImageButtons.map(b => parseInt(b.value.replace(/[^0-9]/g, '')));
      const minPrice = Math.min(...prices);
      const priceItem = item.onImageButtons.find(b => 
        parseInt(b.value.replace(/[^0-9]/g, '')) === minPrice
      );
      return priceItem?.value || 'Contact for price';
    }
    return 'Contact for price';
  };

  // Get capacity from details or direct property
  const getCapacity = () => {
    if (item.capacity) return `${item.capacity} Guests`;
    const capacityDetail = item.details?.find(d => d.label === 'Capacity');
    return capacityDetail?.value || 'N/A';
  };

  // Get child capacity
  const getChildCapacity = () => {
    if (item.childCapacity) return `${item.childCapacity} Children`;
    return null;
  };

  // Get bed types
  const getBedTypes = () => {
    if (item.beds && item.beds.length > 0) {
      return item.beds.map(bed => `${bed.quantity}x ${bed.type}`).join(', ');
    }
    const bedDetail = item.details?.find(d => d.label === 'Beds');
    return bedDetail?.value || 'N/A';
  };

  // Get room size from details
  const getRoomSize = () => {
    const sizeDetail = item.details?.find(d => d.label === 'Size');
    return sizeDetail?.value || 'N/A';
  };

  // Get view type from details
  const getViewType = () => {
    const viewDetail = item.details?.find(d => d.label === 'View');
    return viewDetail?.value || 'Standard';
  };

  // Check if room has child capacity
  const hasChildCapacity = () => {
    return item.childCapacity && item.childCapacity > 0;
  };

  return (
    <div 
      className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer"
      onClick={() => onShowItem(item)}
    >
      <div className="relative h-56 overflow-hidden">
        <img
          src={item.bgImage || item.image}
          alt={item.name}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded-full">
          <span className="text-white text-sm font-semibold">{getBestPrice()}</span>
          <span className="text-white/70 text-xs ml-1">/night</span>
        </div>
        {item.reservedDates && item.reservedDates.length > 0 && (
          <div className="absolute top-3 left-3 bg-red-500/80 backdrop-blur-sm px-3 py-1 rounded-full">
            <span className="text-white text-xs font-semibold">Limited Availability</span>
          </div>
        )}
        {item.rating && (
          <div className="absolute bottom-3 right-3 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full flex items-center gap-1">
            <FaStar className="text-yellow-400 text-xs" />
            <span className="font-secondary text-xs font-semibold text-gray-700">{item.rating}</span>
          </div>
        )}
      </div>
      
      <div className="p-5">
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1">
            <h3 className="font-primary font-bold text-lg text-gray-800 line-clamp-1">
              {item.label || item.name}
            </h3>
            <p className="text-sm text-gray-500 line-clamp-1">{item.provider || 'Villingili Plus'}</p>
          </div>
          <div className="flex items-center gap-1 text-sm text-gray-500 ml-2 flex-shrink-0">
            <FaUsers className="text-blue-500" />
            <span>{getCapacity()}</span>
          </div>
        </div>
        
        <p className="text-sm text-gray-500 leading-relaxed line-clamp-2 mb-3">
          {item.description}
        </p>
        
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded-full flex items-center gap-1">
            <FaBed className="w-3 h-3" />
            {getBedTypes()}
          </span>
          <span className="text-xs bg-green-50 text-green-600 px-2 py-1 rounded-full">
            {getViewType()}
          </span>
          {getRoomSize() !== 'N/A' && (
            <span className="text-xs bg-purple-50 text-purple-600 px-2 py-1 rounded-full">
              {getRoomSize()}
            </span>
          )}
          {hasChildCapacity() && (
            <span className="text-xs bg-yellow-50 text-yellow-600 px-2 py-1 rounded-full flex items-center gap-1">
              <FaChild className="w-3 h-3" />
              {getChildCapacity()}
            </span>
          )}
        </div>
        
        <button 
          className="w-full px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all duration-300 hover:scale-[1.02]"
          onClick={(e) => {
            e.stopPropagation();
            onShowItem(item);
          }}
        >
          View Details
        </button>
      </div>
    </div>
  );
};

export default AccommodationCard;